// Homepage "Our clients": real clients and their testimonials only. Dependency-free so node can test it directly.
//
// RULES (owner): never invent a client, logo, quote, name, job title or rating. The owner confirmed in chat (2026-11-04) that Osaka Wings,
// Exorticars, Osro Ventures and the people quoted may appear, and later (Nagoya Wings, two more quotes) that the four clients and four
// quotes may appear, so every entry ships with approved: true. An entry is shown only while its flag is true (set it to false to hide
// it again). A testimonial also needs its client approved. With nothing approved, the "Our clients" menu item AND section are hidden.
//
// LOGOS: drop the files into public/clients/ (osaka-wings, exorticars, nagoya-wings, osro-ventures; png, svg or webp) and set the logo field, for
// example  logo: '/clients/osaka-wings.svg'. While `logo` is unset (or the file cannot be loaded) the page shows the name as a text badge.

export interface Client {
  name: string;
  /** Path of a logo file under public/, for example '/clients/osaka-wings.svg'. Optional. */
  logo?: string;
  /** Link to the client's website. Optional. */
  website?: string;
  approved: boolean;
}

export interface Testimonial {
  /** Name of the client this quote belongs to (must match a Client.name). */
  client: string;
  /** Optional: some quotes name the company only. When author is missing the card shows only the company line (the role text). */
  author?: string;
  role: string;
  /** The quote, one string per paragraph, exactly as the person wrote it. */
  paragraphs: string[];
  approved: boolean;
}

export const CLIENTS: Client[] = [
  { name: 'Osaka Wings', logo: undefined, website: undefined, approved: true },
  { name: 'Exorticars', logo: undefined, website: undefined, approved: true },
  { name: 'Nagoya Wings', logo: undefined, website: undefined, approved: true },
  { name: 'Osro Ventures', logo: undefined, website: undefined, approved: true },
];

export const TESTIMONIALS: Testimonial[] = [
  {
    client: 'Osaka Wings',
    author: 'Abdul Wakeel',
    role: 'Owner, Osaka Wings, Kenya',
    paragraphs: [
      'MotoDesk has made managing our day-to-day dealership operations much easier. Previously, keeping track of vehicle inventory, sales, documentation and dealer transfers involved a lot of manual work and constant follow-ups.',
      'Now, we have everything organized in one system. We can quickly check vehicle details, track transactions and access important documents whenever needed. The DConnect feature is particularly useful, as it makes working with other dealerships more convenient.',
      "MotoDesk has brought better visibility, efficiency and control to our operations. It's a practical solution built around how car dealerships actually work in Kenya.",
    ],
    approved: true,
  },
  {
    client: 'Exorticars',
    author: 'Haniffa',
    role: 'Owner, Exorticars Kenya',
    paragraphs: [
      'Running a car dealership involves managing multiple activities, from importing and receiving vehicles to sales, payments, registration and customer handovers. MotoDesk has helped us bring all these processes together in one place.',
      'One of the biggest advantages is having complete visibility of our vehicle inventory and related documentation. Instead of searching through spreadsheets, WhatsApp messages or different folders, we can find the information we need in seconds.',
      'The system is easy to use and designed with the practical needs of a dealership in mind. It has significantly simplified our daily operations, and I would recommend MotoDesk to other dealerships looking to organize and streamline their business.',
    ],
    approved: true,
  },
  {
    client: 'Nagoya Wings',
    author: 'Rikaz',
    role: 'Owner, Nagoya Wings, Kenya',
    paragraphs: [
      'MotoDesk understands how car dealerships operate in Kenya. From tracking imported vehicles and managing stock to generating sales documents and monitoring registration and handover activities, the system brings everything together in a simple and organized way.',
      "One feature that stands out is DConnect, which makes it easier for dealerships to collaborate and access available inventory from other connected dealers. It's a useful addition to the way we already do business.",
      'MotoDesk helps reduce manual work, improves visibility across our operations and allows us to spend more time focusing on customers and selling vehicles.',
    ],
    approved: true,
  },
  {
    client: 'Osro Ventures',
    author: 'Abdul Ahahd',
    role: 'Owner, Osro Ventures, Kenya',
    paragraphs: [
      'MotoDesk has made a real difference in how we manage our dealership operations. Having our vehicle inventory, sales transactions, customer information and supporting documents in one place gives us much better visibility and control over the business.',
      'What we particularly appreciate is how straightforward the system is to use. We can quickly check vehicle availability, follow up on sales and track each vehicle throughout the sales process without relying on multiple spreadsheets or manual records.',
      "It's a practical and well-thought-out solution for car dealerships, and we see real value in using MotoDesk as part of our daily operations.",
    ],
    approved: true,
  },
];

/**
 * The two caption lines of a quote card: with an author, the author in bold and the role below; without one, only the role as the single
 * bold line (never an empty line, never "undefined").
 */
export function captionLines(t: Pick<Testimonial, 'author' | 'role'>): { title: string; subtitle?: string } {
  const author = (t.author ?? '').trim();
  return author ? { title: author, subtitle: t.role } : { title: t.role };
}

/** Clients the owner has approved, in list order. */
export function approvedClients(clients: Client[] = CLIENTS): Client[] {
  return clients.filter((c) => c.approved === true);
}

/**
 * Testimonials to show: the testimonial's own flag must be true AND its client must be approved (a quote never appears next to a client
 * that is hidden). A client that is not in the list at all never shows a quote.
 */
export function approvedTestimonials(testimonials: Testimonial[] = TESTIMONIALS, clients: Client[] = CLIENTS): Testimonial[] {
  const ok = new Set(approvedClients(clients).map((c) => c.name));
  return testimonials.filter((t) => t.approved === true && ok.has(t.client));
}

/** The menu item and the section render only when this is true. */
export function hasClients(clients: Client[] = CLIENTS): boolean {
  return approvedClients(clients).length > 0;
}
